import * as React from 'react';
import Tag from 'antd/lib/tag';
import Avatar from 'antd/lib/avatar';
import Collapse from 'antd/lib/collapse';
import Rate from 'antd/lib/rate';
import Text from 'antd/lib/typography/Text';
import Paragraph from 'antd/lib/typography/Paragraph';
import dayjs from 'dayjs';
import { ExportOutlined, TagFilled } from '@ant-design/icons';
import { IssueSource, IssueSourceType } from '@/types';
import { antGrey, getAvatarColorForName, rwColors } from '@/components/styles/globalStyles';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import runwayLogo from '../../../assets/integration-runway.svg';

const ROW_STYLE: React.CSSProperties = {
    margin: '0 16px',
    padding: '12px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
};

const ROW_BORDER_STYLE: React.CSSProperties = { ...ROW_STYLE, borderTop: `1px solid ${antGrey.grey4}` };

const personRow = (label: string, name?: string) => {
    if (!name) {
        return null;
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{label}</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar
                    size={20}
                    style={{ backgroundColor: getAvatarColorForName(name), fontSize: 11, border: 'none' }}
                >
                    {name.charAt(0).toUpperCase()}
                </Avatar>
                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{name}</Text>
            </div>
        </div>
    );
};

const PMTicketSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const ticket = source.pmTicket;
    if (!ticket) {
        return null;
    }
    return (
        <>
            <div style={ROW_STYLE}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                    <Text style={{ fontSize: 16, color: antGrey.grey9 }}>{ticket.ticketTitle}</Text>
                    {ticket.ticketUrl && (
                        <a
                            href={ticket.ticketUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                        >
                            <TagFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />
                            <Text style={{ color: rwColors.primary, fontSize: 14 }}>{ticket.ticketId}</Text>
                        </a>
                    )}
                </div>
                {ticket.labels?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {ticket.labels.map((label) => (
                            <Tag
                                key={label}
                                style={{
                                    margin: 0,
                                    background: antGrey.grey4,
                                    border: `1px solid ${antGrey.grey4}`,
                                    color: antGrey.grey8,
                                }}
                            >
                                {label}
                            </Tag>
                        ))}
                    </div>
                )}
            </div>
            <div style={ROW_BORDER_STYLE}>
                {ticket.ticketDescription && (
                    <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                        {ticket.ticketDescription}
                    </Paragraph>
                )}
                {personRow('Assignee', ticket.assignee)}
            </div>
            {ticket.reporter && <div style={ROW_BORDER_STYLE}>{personRow('Reporter', ticket.reporter)}</div>}
        </>
    );
};

const UserReviewsSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.userReviews;
    if (!data) {
        return null;
    }
    const reviews = data.sampleReviews ?? [];
    if (reviews.length === 0) {
        return (
            <div style={ROW_STYLE}>
                <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                    {data.aiDescription}
                </Paragraph>
            </div>
        );
    }
    return (
        <>
            {reviews.map((review, index) => (
                <div key={index} style={index === 0 ? ROW_STYLE : ROW_BORDER_STYLE}>
                    <Rate
                        disabled
                        value={Number(review.rating) || 0}
                        style={{ fontSize: 14, color: rwColors.statusYellow }}
                    />
                    {review.title && (
                        <Text style={{ fontSize: 14, color: antGrey.grey9, fontWeight: 600 }}>{review.title}</Text>
                    )}
                    {review.content && (
                        <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                            {review.content}
                        </Paragraph>
                    )}
                    <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                        {review.author}
                        {review.submittedAt ? ` · ${dayjs(review.submittedAt).format('MMM D, YYYY')}` : ''}
                    </Text>
                </div>
            ))}
        </>
    );
};

const CONDITION_SYMBOL: Record<string, string> = {
    LessThan: '<',
    LessThanOrEqualTo: '≤',
    GreaterThan: '>',
    GreaterThanOrEqualTo: '≥',
};

const metricRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{label}</Text>
        <Text style={{ color: antGrey.grey9, fontSize: 14 }}>{value}</Text>
    </div>
);

const HealthMetricsSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.healthMetrics;
    if (!data) {
        return null;
    }
    const threshold = data.configuredThreshold;
    return (
        <>
            <div style={ROW_STYLE}>
                {metricRow('Current value', data.currentValue)}
                {threshold &&
                    metricRow('Threshold', `${CONDITION_SYMBOL[threshold.condition] ?? ''} ${threshold.value}`.trim())}
            </div>
            {data.build && <div style={ROW_BORDER_STYLE}>{metricRow('Build', `#${data.build}`)}</div>}
        </>
    );
};

const RunwaySource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.runway;
    if (!data) {
        return null;
    }
    return (
        <>
            {data.description && (
                <div style={ROW_STYLE}>
                    <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                        {data.description}
                    </Paragraph>
                </div>
            )}
            {data.build && <div style={ROW_BORDER_STYLE}>{metricRow('Build', `#${data.build}`)}</div>}
        </>
    );
};

const RegressionTestingSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.regressionTesting;
    if (!data) {
        return null;
    }
    return (
        <>
            <div style={ROW_STYLE}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                    <Text style={{ fontSize: 16, color: antGrey.grey9 }}>{data.testRunName || 'Test run'}</Text>
                    {data.testRunUrl && (
                        <a
                            href={data.testRunUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                        >
                            <ExportOutlined style={{ color: rwColors.primary, fontSize: 14 }} />
                            <Text style={{ color: rwColors.primary, fontSize: 14 }}>View run</Text>
                        </a>
                    )}
                </div>
                {data.failedTestCaseName && metricRow('Failed test case', data.failedTestCaseName)}
            </div>
            {data.build && <div style={ROW_BORDER_STYLE}>{metricRow('Build', `#${data.build}`)}</div>}
        </>
    );
};

const SourceFooter: React.FC<{ createdLabel: string; updatedLabel: string }> = ({ createdLabel, updatedLabel }) => (
    <div style={ROW_BORDER_STYLE}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Text style={{ color: antGrey.grey7, fontSize: 12 }}>Created {createdLabel}</Text>
            <Text style={{ color: antGrey.grey7, fontSize: 12 }}>Updated {updatedLabel}</Text>
        </div>
    </div>
);

export const MetadataSourceSection: React.FC<{
    source: IssueSource;
    createdLabel: string;
    updatedLabel: string;
}> = ({ source, createdLabel, updatedLabel }) => {
    let title = '';
    let body: React.ReactNode = null;
    let icon: React.ReactNode = <IntegrationIcon integrationId={source.integrationType} size={18} />;
    if (source.type === IssueSourceType.PMTicket && source.pmTicket) {
        title = 'Ticket';
        body = <PMTicketSource source={source} />;
    } else if (source.type === IssueSourceType.UserReviews && source.userReviews) {
        title = 'User reviews';
        body = <UserReviewsSource source={source} />;
    } else if (source.type === IssueSourceType.HealthMetrics && source.healthMetrics) {
        title = 'Health metric';
        body = <HealthMetricsSource source={source} />;
    } else if (source.type === IssueSourceType.Runway && source.runway) {
        title = 'Runway';
        body = <RunwaySource source={source} />;
        icon = <img src={runwayLogo} width={18} height={18} alt="Runway" />;
    } else if (source.type === IssueSourceType.RegressionTesting && source.regressionTesting) {
        title = 'Regression testing';
        body = <RegressionTestingSource source={source} />;
    }
    if (!body) {
        return null;
    }
    return (
        <Collapse
            className="triage-collapse"
            ghost
            bordered={false}
            defaultActiveKey={['section']}
            items={[
                {
                    key: 'section',
                    showArrow: false,
                    style: { borderTop: `1px solid ${antGrey.grey4}` },
                    label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            {icon}
                            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{title}</Text>
                        </div>
                    ),
                    children: (
                        <>
                            {body}
                            <SourceFooter createdLabel={createdLabel} updatedLabel={updatedLabel} />
                        </>
                    ),
                },
            ]}
        />
    );
};
